interface LifeManagerConfig {
  maxLives: number;
  refillInterval: number; // in milliseconds
  storageKey: string;
}

interface LifeState {
  currentLives: number;
  lastUpdateTime: number;
  lastLifeLostTime?: number;
  maxLives?: number;
}

export class LifeManager {
  private config: LifeManagerConfig;
  private state: LifeState;
  private refillTimer?: NodeJS.Timeout;
  private updateCallbacks: Set<(lives: number) => void> = new Set();

  constructor(config: Partial<LifeManagerConfig> = {}) {
    this.config = {
      maxLives: 9999,
      refillInterval: 30 * 60 * 1000, // 30 minutes
      storageKey: 'lifeManagerState',
      ...config
    };

    // Load state from localStorage
    this.state = this.loadState();

    // If a max lives value was persisted, honor it.
    if (typeof this.state.maxLives === 'number' && Number.isFinite(this.state.maxLives)) {
      this.config.maxLives = Math.max(1, Math.floor(this.state.maxLives));
      this.state.currentLives = Math.min(this.state.currentLives, this.config.maxLives);
    } else {
      this.state.maxLives = this.config.maxLives;
    }
    
    // Calculate lives based on time passed
    this.calculateLivesFromTimePassed();
    
    // Start the refill timer
    this.startRefillTimer();

    // Save state on visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.saveState();
      } else {
        this.calculateLivesFromTimePassed();
      }
    });

    // Save state before unload
    window.addEventListener('beforeunload', () => {
      this.saveState();
    });
  }

  private loadState(): LifeState {
    try {
      const saved = localStorage.getItem(this.config.storageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load life state:', e);
    }

    return {
      currentLives: this.config.maxLives,
      lastUpdateTime: Date.now()
    };
  }

  private saveState(): void {
    try {
      localStorage.setItem(
        this.config.storageKey,
        JSON.stringify({ ...this.state, maxLives: this.config.maxLives })
      );
    } catch (e) {
      console.error('Failed to save life state:', e);
    }
  }

  private calculateLivesFromTimePassed(): void {
    if (this.state.currentLives >= this.config.maxLives) {
      this.state.lastUpdateTime = Date.now();
      return;
    }

    const now = Date.now();
    const timePassed = now - this.state.lastUpdateTime;
    const livesToAdd = Math.floor(timePassed / this.config.refillInterval);

    if (livesToAdd > 0) {
      const previousLives = this.state.currentLives;
      this.state.currentLives = Math.min(
        this.state.currentLives + livesToAdd,
        this.config.maxLives
      );
      this.state.lastUpdateTime = now - (timePassed % this.config.refillInterval);
      
      if (this.state.currentLives !== previousLives) {
        this.notifyUpdate();
        this.saveState();
      }
    }
  }

  private startRefillTimer(): void {
    this.stopRefillTimer();

    const checkRefill = () => {
      this.calculateLivesFromTimePassed();
      
      if (this.state.currentLives < this.config.maxLives) {
        const timeSinceLastUpdate = Date.now() - this.state.lastUpdateTime;
        const timeUntilNextLife = this.config.refillInterval - timeSinceLastUpdate;
        
        this.refillTimer = setTimeout(() => {
          this.state.currentLives = Math.min(
            this.state.currentLives + 1,
            this.config.maxLives
          );
          this.state.lastUpdateTime = Date.now();
          this.notifyUpdate();
          this.saveState();
          checkRefill();
        }, Math.max(0, timeUntilNextLife));
      }
    };

    checkRefill();
  }

  private stopRefillTimer(): void {
    if (this.refillTimer) {
      clearTimeout(this.refillTimer);
      this.refillTimer = undefined;
    }
  }

  private notifyUpdate(): void {
    this.updateCallbacks.forEach(callback => callback(this.state.currentLives));
  }

  public getLives(): number {
    this.calculateLivesFromTimePassed();
    return this.state.currentLives;
  }

  public getMaxLives(): number {
    return this.config.maxLives;
  }

  public setMaxLives(maxLives: number): void {
    const nextMax = Math.max(1, Math.floor(maxLives));
    if (nextMax === this.config.maxLives) return;

    this.config.maxLives = nextMax;
    this.state.maxLives = nextMax;
    this.state.currentLives = Math.min(this.state.currentLives, nextMax);
    this.state.lastUpdateTime = Date.now();

    this.notifyUpdate();
    this.saveState();
    this.startRefillTimer();
  }

  public setLives(lives: number): void {
    const newLives = Math.max(0, Math.min(lives, this.config.maxLives));
    if (newLives !== this.state.currentLives) {
      this.state.currentLives = newLives;
      this.state.lastUpdateTime = Date.now();
      this.notifyUpdate();
      this.saveState();
      this.startRefillTimer();
    }
  }

  public loseLife(): boolean {
    if (this.state.currentLives > 0) {
      this.state.currentLives--;
      this.state.lastUpdateTime = Date.now();
      this.state.lastLifeLostTime = Date.now();
      this.notifyUpdate();
      this.saveState();
      this.startRefillTimer();
      return this.state.currentLives > 0;
    }
    return false;
  }

  public addLives(amount: number): void {
    this.setLives(this.state.currentLives + amount);
  }

  public getTimeUntilNextLife(): number {
    if (this.state.currentLives >= this.config.maxLives) {
      return 0;
    }

    const timeSinceLastUpdate = Date.now() - this.state.lastUpdateTime;
    return Math.max(0, this.config.refillInterval - timeSinceLastUpdate);
  }

  public getFormattedTimeUntilNextLife(): string {
    const timeLeft = this.getTimeUntilNextLife();
    if (timeLeft === 0) {
      return this.state.currentLives >= this.config.maxLives ? 'Max lives' : 'Ready!';
    }

    const minutes = Math.floor(timeLeft / 60000);
    const seconds = Math.floor((timeLeft % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  public onUpdate(callback: (lives: number) => void): () => void {
    this.updateCallbacks.add(callback);
    // Return unsubscribe function
    return () => {
      this.updateCallbacks.delete(callback);
    };
  }

  public destroy(): void {
    this.stopRefillTimer();
    this.updateCallbacks.clear();
  }
}

// Singleton instance
let lifeManagerInstance: LifeManager | null = null;

export function getLifeManager(): LifeManager {
  if (!lifeManagerInstance) {
    lifeManagerInstance = new LifeManager();
  }
  return lifeManagerInstance;
}

export function resetLifeManager(): void {
  if (lifeManagerInstance) {
    lifeManagerInstance.destroy();
    lifeManagerInstance = null;
  }
}
